import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api, type PageResult } from '@/lib/api'

/**
 * Phase 1 Users is read-only ("view" tag in the prototype nav).
 * Shape follows the internal tech spec §4 + §9.2: `users` has no email column,
 * so the console's "email" comes from the user's linked `auth_identities`.
 *
 * No row-level scoping: both console roles receive every user, staff included.
 * Role only gates the Administration module.
 */
/**
 * Mirrors `UserRow` in docs/api-contract.md field for field.
 *
 * **snake_case on the wire**, matching the convention cwc-backend-v1 already uses
 * everywhere (`json:"display_name"`, `json:"avatar_url"`). The Phase 1 spec's
 * payload examples were camelCase; they have been corrected to snake_case so the
 * docs and the platform agree.
 */
/**
 * The role *code*, not a number.
 *
 * This is deliberately unlike `UserDto.role` on `GET /me`, which is a tinyint —
 * that endpoint belongs to the existing consumer backend, where `users.role` is
 * a real column. The admin endpoints are net-new and derive role by joining
 * `staff_user_roles` → `staff_roles.code`, which is a `varchar`. So the wire value
 * here is the code, and `member` is what the backend synthesises for a user with
 * no role row at all.
 */
export type UserRoleCode = 'member' | 'ops_admin' | 'super_admin'

export interface AdminUser {
  /** users.public_id — opaque string, not the numeric PK. */
  id: string
  display_name: string
  email: string
  /** Derived from `staff_user_roles`; there is no `users.role` for the console. */
  role: UserRoleCode
  /** users.status — 1 = active, 0 = disabled. */
  status: number
  /** OAuth providers on auth_identities, e.g. ['google', 'apple']. */
  linked_accounts: string[]
  /** SUM(points_ledger.amount) — computed, not a stored column. */
  points_balance: number
  joined_at: string
}

/**
 * A row on the append-only ledger. `amount` is **signed** — positive is a credit,
 * negative a debit from the future rewards shop — so a balance is `SUM(amount)`
 * and is never stored. The console only ever reads this.
 */
export interface LedgerEntry {
  id: number
  amount: number
  reason: 'quest_reward' | 'manual_adjustment' | 'shop_purchase'
  /** What caused it, e.g. `quest`. Null for adjustments with no referent. */
  ref_type: string | null
  ref_id: number | null
  created_at: string
}

/** `quest_member_tasks.state` — 1..4 in the column, the code on the wire. */
export type MemberTaskState = 'pending' | 'submitted' | 'verified' | 'rejected'

/** `quest_member_progress.state`. */
export type MemberQuestState =
  | 'in_progress'
  | 'awaiting_reward'
  | 'rewarded'
  | 'reward_rejected'

export interface MemberTask {
  task_id: number
  title: string
  state: MemberTaskState
  /**
   * The three below are **requested, not yet in the contract** (spec Q21). They
   * already exist as columns on `quest_member_tasks`, and without them the screen
   * can say a task was rejected but not who rejected it or why — which is most
   * of what someone opens this page to find out. Optional so the screen degrades
   * quietly rather than breaking if the backend ships the narrow shape.
   */
  note?: string | null
  verified_by?: string | null
  verified_at?: string | null
}

export interface MemberQuest {
  quest_id: number
  name: string
  state: MemberQuestState
  completed_at?: string | null
  tasks: MemberTask[]
}

/**
 * `GET /users/{id}`.
 *
 * Carries the points **balance only**. The ledger is unbounded — it grows for the
 * life of the account — so it has its own paginated route (spec Q19). Quest
 * progress stays inline because its size is bounded by how many quests exist.
 * That is the general rule: unbounded collections get a route, bounded ones stay
 * embedded.
 *
 * What this payload deliberately does **not** contain: any quest the member has
 * not touched. Progress rows are created lazily on first qualifying action, so
 * publishing a quest writes nothing and an untouched quest is simply absent (Q20).
 */
export interface UserDetail {
  id: string
  display_name: string
  email: string
  avatar_url: string
  role: UserRoleCode
  /**
   * `users.status` — 1 active, 0 disabled.
   *
   * **Returned but deliberately not rendered on this screen yet** (spec Q23). The
   * field is here so the payload is complete and consistent with `GET /users`,
   * which does render it; the detail-side badge needs no backend change and can
   * follow whenever someone asks. Requesting it now is what avoids a second
   * round-trip to the backend later.
   */
  status: number
  joined_at: string
  linked_accounts: Array<{ provider: string; email: string }>
  /**
   * Null when the member has never bound a UID. Kept separate from
   * `linked_accounts` even though both live on `auth_identities`: collapsing them
   * would render the same binding twice, and Biconomy is not a sign-in method.
   */
  biconomy: { bound: boolean; uid?: string } | null
  /**
   * Null when the member has never entered a handle. Manually typed, not
   * OAuth — uniqueness-checked only (`uk_provider_uid`), same as Biconomy.
   */
  twitter: { bound: boolean; handle?: string } | null
  points: { balance: number }
  quests: MemberQuest[]
}

export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: ['users', 'detail', id],
    queryFn: () => api.get<UserDetail>(`/admin/users/${id}`),
    enabled: id !== undefined,
  })
}

/**
 * `GET /users/{id}/ledger` — its own route rather than an array on the detail
 * payload, because the ledger has no ceiling (spec Q19).
 *
 * Being a real paginated query is the point: it goes through DataTable's
 * server-side pagination like every other list in the console, instead of an
 * inline array that had to be wrapped in a fake query object to render.
 */
export function useUserLedger(
  id: string | undefined,
  { page = 1, pageSize = 20 }: { page?: number; pageSize?: number } = {},
) {
  return useQuery({
    queryKey: ['users', 'ledger', id, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      return api.get<PageResult<LedgerEntry>>(`/admin/users/${id}/ledger?${params}`)
    },
    enabled: id !== undefined,
    // Keeps rows on screen while the next page loads instead of flashing empty.
    placeholderData: keepPreviousData,
  })
}

export interface UsersQuery {
  page?: number
  pageSize?: number
  /** Matches display name, email, or public_id — applied server-side. */
  q?: string
}

export function useUsers({ page = 1, pageSize = 30, q = '' }: UsersQuery = {}) {
  return useQuery({
    queryKey: ['users', page, pageSize, q],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      if (q) params.set('q', q)
      return api.get<PageResult<AdminUser>>(`/admin/users?${params}`)
    },
    // Keeps the table populated while the next page loads instead of flashing empty.
    placeholderData: keepPreviousData,
  })
}
